import { formatUPosition, parseUPosition } from "../helpers/hardwareContract";
import { ASSET_TYPES, BIZ_TYPES } from "../helpers/assetOptions";
import { DTO_FIELDS } from "../helpers/dtoWhitelist";

/**
 * 硬體 mock store 的一筆 = **後端 DTO 形狀**（票 poc-20260922/07）。
 *
 * 存的鍵是 swagger 的 `DcHardwareDto` 可寫欄位，加上 `DcHardwareListItemDto` 才有、又不能由別的資料推導的
 * 那幾個（`ip_address`、`status_name`、CVE／KEV 等級與命中數）。後端**算出來的**東西不存，
 * 回傳時才算（與後端同一個分工）：機房／機櫃編號（由 `rack_id` 查）、EOS／EOL 風險等級（由日期）、
 * 管理者姓名（由負責關係）、業務／資產類別名稱（由 id 查主檔）、作業系統／應用系統清單（由 id 查軟體）。
 *
 * 後端**沒有**的東西（兩段式上架階段、替代機、IP 分頁、結束攤提日、異動歷程、弱掃明細、審核單號…）
 * 收在每筆的 `mock_only`，API handler 一律不吐 —— 畫面上對應的欄位只在開發環境顯示。
 *
 * 審核引擎、U 位計算、`/api/hardware` 舊資源等還講前端欄位名的消費者，經 `hardwareCodec` 讀寫
 * （`crudFactory` 的 `codec`），不直接碰 DTO。
 */

/** 前端舊欄位名 ↔ DTO 鍵，值原樣搬（不轉型） */
const SAME_VALUE = [
  ["code", "asset_no"], ["name", "host_name"], ["serialNo", "hardware_no"],
  ["brand", "brand"], ["brandId", "brand_id"], ["model", "model"], ["modelId", "model_id"],
  ["mac", "mac_address"], ["ip", "ip_address"],
  ["cveLevel", "cve_risk_level"], ["kevLevel", "kev_risk_level"],
  ["cveCount", "cve_count"], ["kevCount", "kev_count"],
  ["vulnerabilityHits", "vulnerability_hits"],
  ["firmwareVersion", "firmware_version"], ["firmwareOsName", "firmware_os_name"],
  ["firmwareOsVersion", "firmware_os_version"], ["cpe", "cpe"], ["firmwareCpe", "firmware_cpe"],
  ["latestVersion", "latest_version"],
  ["interfaceCount", "interface_count"], ["powerCount", "power_count"], ["cpuCores", "cpu_core_count"],
  ["ramGb", "ram_gb"], ["storageTb", "disk_capacity_tb"],
  ["description", "remark"], ["propertyNo", "property_no"],
  ["purchaseAmount", "purchase_amount"], ["currency", "currency_code"],
  ["vmBrand", "virtual_brand"], ["vmCount", "virtual_machine_count"],
  ["deployCpuCores", "virtual_cpu_core_count"], ["deployRamGb", "virtual_ram_gb"],
  ["deployStorageTb", "virtual_disk_capacity_tb"],
  ["operatingSystemIds", "operating_system_ids"], ["applicationSystemIds", "application_system_ids"],
  ["createdOn", "created_on"], ["updatedOn", "updated_on"],
];
/** 日期欄:後端是 date-time;舊消費者用 YYYY-MM-DD */
const DATES = [
  ["eos", "eos_date"], ["eol", "eol_date"], ["endOfSale", "end_of_sale_date"],
  ["cveDate", "cve_date"], ["kevDate", "kev_date"],
  ["purchaseDate", "purchase_date"], ["mountDate", "mounted_date"],
];
/** 讀取時才加上去的衍生值:寫回時丟掉,不進 store(不然會變成第二份真相) */
const DERIVED = new Set([
  "id", "manager", "managerId", "roomName", "floorName", "areaName", "roomId", "cabinetCode",
  "uEnd", "positionRaw", "bizTypeId", "assetTypeId", "keyBiz", "assetCode",
  "operatingSystemRefs", "applicationSystemRefs", "os", "appSystems",
]);
const HANDLED = new Set([
  ...SAME_VALUE.map(([k]) => k), ...DATES.map(([k]) => k),
  "starred", "cabinetId", "rackId", "uPosition", "uSize", "status", "bizType", "assetType", "scan",
]);

/** YYYY-MM-DD → 後端的 date-time 字串;已經是 date-time 或空值原樣 */
export const toDateTime = (v) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T00:00:00` : (v || null));
const dateOnly = (v) => (typeof v === "string" ? v.slice(0, 10) : v || "");

/** 後端的狀態字(運作中／維修中／已停用) ↔ 前端舊狀態字(運行中／維修中／停用;「閒置」後端沒有) */
const STATUS_TO_API = { 運行中: "運作中", 維修中: "維修中", 停用: "已停用", 閒置: "運作中" };
const STATUS_FROM_API = { 運作中: "運行中", 維修中: "維修中", 已停用: "停用" };

const nameById = (list, id) => (Number(id) > 0 ? list[Number(id) - 1] || "" : "");
const idByName = (list, name) => list.indexOf(name) + 1 || 0;

/** stored(DTO)→ 舊消費者的形狀 */
export function hardwareToLegacy(row) {
  if (!row) return row;
  const m = row.mock_only || {};
  const out = { ...m, id: row.id };
  SAME_VALUE.forEach(([k, d]) => { if (row[d] !== undefined) out[k] = row[d]; });
  DATES.forEach(([k, d]) => { if (row[d] !== undefined) out[k] = dateOnly(row[d]); });
  const u = parseUPosition(row.position_u);
  out.cabinetId = Number(row.rack_id) || null;
  out.uPosition = u.start === null ? "" : u.start;
  out.uSize = Number(row.occupied_u) || (u.start !== null ? u.end - u.start + 1 : 1);
  out.starred = !!row.is_key_business;
  out.status = m.status || STATUS_FROM_API[row.status_name] || (row.active === false ? "停用" : "運行中");
  out.bizType = nameById(BIZ_TYPES, row.business_category_id);
  out.assetType = nameById(ASSET_TYPES, row.asset_category_id);
  out.scan = {
    date: dateOnly(row.vulnerability_scan_date),
    nextDate: dateOnly(row.next_vulnerability_scan_date),
    files: m.scanFiles || [],
    rows: m.scanRows || [],
  };
  delete out.scanFiles;
  delete out.scanRows;
  return out;
}

/** 舊消費者寫進來的欄位(整筆或局部)→ 新的 stored row */
export function hardwareFromLegacy(fields, prev = {}) {
  const next = { ...prev };
  const m = { ...(prev.mock_only || {}) };
  SAME_VALUE.forEach(([k, d]) => { if (fields[k] !== undefined) next[d] = fields[k]; });
  DATES.forEach(([k, d]) => { if (fields[k] !== undefined) next[d] = toDateTime(fields[k]); });
  if (fields.starred !== undefined) next.is_key_business = !!fields.starred;
  const rack = fields.cabinetId ?? fields.rackId;
  if (rack !== undefined) next.rack_id = Number(rack) || 0;
  if (fields.uSize !== undefined) next.occupied_u = Number(fields.uSize) || 0;
  if (fields.uPosition !== undefined || fields.uSize !== undefined) {
    const start = fields.uPosition !== undefined ? fields.uPosition : parseUPosition(prev.position_u).start;
    next.position_u = formatUPosition(start, next.occupied_u) || "";
  }
  if (fields.status !== undefined) {
    next.status_name = STATUS_TO_API[fields.status] || "運作中";
    next.active = fields.status !== "停用";
    if (fields.status === "閒置") m.status = "閒置"; else delete m.status;
  }
  if (fields.bizType !== undefined) next.business_category_id = idByName(BIZ_TYPES, fields.bizType);
  if (fields.assetType !== undefined) next.asset_category_id = idByName(ASSET_TYPES, fields.assetType);
  if (fields.scan !== undefined) {
    const sc = fields.scan || {};
    next.vulnerability_scan_date = toDateTime(sc.date);
    next.next_vulnerability_scan_date = toDateTime(sc.nextDate);
    m.scanFiles = sc.files || [];
    m.scanRows = sc.rows || [];
  }
  Object.keys(fields).forEach((k) => {
    if (HANDLED.has(k) || DERIVED.has(k) || k === "mock_only") return;
    if (/^[a-z]+(_[a-z0-9]+)+$/.test(k)) return; // DTO 鍵走 raw 寫入,不從舊入口混進來
    m[k] = fields[k];
  });
  next.mock_only = m;
  return next;
}

export const hardwareCodec = { toView: hardwareToLegacy, toStore: hardwareFromLegacy };

/** Create／Update 收下的鍵:DcHardwareDto 裡使用者寫得進去的(名稱鏡像、唯讀清單、時間戳由後端給) */
const READ_ONLY = new Set([
  "id", "created_on", "updated_on", "business_category_name", "asset_category_name",
  "manager_customer_name", "operating_systems", "application_systems", "vulnerability_hits",
  "cve_date", "kev_date", "latest_version", "room_id",
]);
export const HARDWARE_WRITABLE = DTO_FIELDS.DcHardwareDto.filter((k) => !READ_ONLY.has(k));
const DATE_KEYS = new Set([
  "purchase_date", "mounted_date", "eos_date", "eol_date", "end_of_sale_date",
  "vulnerability_scan_date", "next_vulnerability_scan_date",
]);

/** payload(DcHardwareDto)→ 存進 store 的欄位:只收可寫鍵,日期補成 date-time */
export function hardwareFieldsFromDto(dto) {
  const out = {};
  HARDWARE_WRITABLE.forEach((k) => {
    if (dto[k] === undefined) return;
    out[k] = DATE_KEYS.has(k) ? toDateTime(dto[k]) : dto[k];
  });
  if (out.active !== undefined && out.status_name === undefined) {
    out.status_name = out.active ? "運作中" : "已停用";
  }
  return out;
}

/** 列表列 DTO 的鍵(swagger DcHardwareListItemDto),依 swagger 原序 */
export const HARDWARE_LIST_ITEM_KEYS = [
  "asset_no", "hardware_no", "host_name", "is_key_business", "brand", "model",
  "room_id", "room_name", "rack_id", "rack_no", "position_u", "ip_address",
  "eos_date", "eos_risk_level", "eol_date", "eol_risk_level",
  "cve_date", "cve_risk_level", "cve_count", "kev_date", "kev_risk_level", "kev_count",
  "manager_customer_id", "manager_customer_name", "status_name", "is_under_review",
  "approval_request_no", "id",
];

const DAY = 24 * 60 * 60 * 1000;
/**
 * EOS／EOL 依剩餘天數換算等級。⚠️ 門檻是 **mock 自己編的** —— 後端的規則沒寫在 swagger(票 21 待問)。
 * 這裡扮演後端,前端仍只讀不算(ADR-0007)。
 */
export const riskLevelByDate = (dateStr) => {
  if (!dateStr) return null; // 沒有日期 = 沒有這個風險,不是「低風險」
  const days = Math.floor((new Date(dateStr) - Date.now()) / DAY);
  if (days <= 90) return "嚴重"; // 含已過期(負數)
  if (days <= 180) return "高";
  if (days <= 365) return "中";
  if (days <= 730) return "低";
  return null;
};

/**
 * 回傳用的投影。`ctx` 給後端要查別的表才算得出來的東西:
 *   rack(id)      → { roomId, code }（機櫃,舊形狀）
 *   room(id)      → { name }
 *   manager(id)   → { managerId, manager }（負責關係）
 *   softwareRefs(ids) → DcSoftwareRefDto[]
 */
export function hardwareListItemDto(row, ctx) {
  const m = row.mock_only || {};
  const rack = ctx.rack(row.rack_id) || {};
  const room = ctx.room(rack.roomId) || {};
  const mgr = ctx.manager(row.id);
  const pending = m.approvalStatus === "pending";
  return {
    asset_no: row.asset_no || "",
    hardware_no: row.hardware_no || "",
    host_name: row.host_name || "",
    is_key_business: !!row.is_key_business,
    brand: row.brand || "",
    model: row.model || "",
    room_id: Number(rack.roomId) || 0,
    room_name: room.name || "",
    rack_id: Number(row.rack_id) || 0,
    rack_no: rack.code || "",
    position_u: row.position_u || "",
    ip_address: row.ip_address || "",
    eos_date: row.eos_date || null,
    eos_risk_level: riskLevelByDate(row.eos_date),
    eol_date: row.eol_date || null,
    eol_risk_level: riskLevelByDate(row.eol_date),
    cve_date: row.cve_date || null,
    cve_risk_level: row.cve_risk_level || null,
    cve_count: Number(row.cve_count) || 0,
    kev_date: row.kev_date || null,
    kev_risk_level: row.kev_risk_level || null,
    kev_count: Number(row.kev_count) || 0,
    manager_customer_id: Number(mgr.managerId) || 0,
    manager_customer_name: mgr.manager || "",
    status_name: row.status_name || "運作中",
    // 後端語意:審核中才有單號(swagger「審核中之單據編號」)
    is_under_review: pending,
    approval_request_no: pending ? m.docNo || "" : "",
    id: row.id,
  };
}

/** 編輯頁 DTO:鍵集合＝swagger DcHardwareDto,一個不多一個不少 */
export function hardwareDetailDto(row, ctx) {
  const rack = ctx.rack(row.rack_id) || {};
  const mgr = ctx.manager(row.id);
  const derived = {
    business_category_name: nameById(BIZ_TYPES, row.business_category_id),
    asset_category_name: nameById(ASSET_TYPES, row.asset_category_id),
    manager_customer_id: Number(mgr.managerId) || 0,
    manager_customer_name: mgr.manager || "",
    room_id: Number(rack.roomId) || 0,
    operating_systems: ctx.softwareRefs(row.operating_system_ids),
    application_systems: ctx.softwareRefs(row.application_system_ids),
  };
  const DEFAULTS = {
    business_category_id: 0, is_key_business: false, property_no: "", host_name: "",
    asset_category_id: 0, asset_no: "", brand_id: 0, brand: "", model_id: 0, model: "",
    hardware_no: "", purchase_date: null, purchase_amount: 0, currency_code: "TWD",
    mounted_date: null, mac_address: "", rack_id: 0, position_u: "", active: true,
    created_on: "2026-08-06T23:36:47+08:00", updated_on: "2026-08-30T18:19:08+08:00",
    interface_count: 0, firmware_version: "", occupied_u: 0, power_count: 0, cpu_core_count: 0,
    ram_gb: 0, disk_capacity_tb: 0, operating_system_ids: [], remark: "",
    eos_date: null, eol_date: null, end_of_sale_date: null, latest_version: null,
    firmware_os_name: null, firmware_os_version: null, cpe: null, firmware_cpe: null,
    cve_date: null, kev_date: null, vulnerability_hits: [],
    vulnerability_scan_date: null, vulnerability_scan_file_url: null, next_vulnerability_scan_date: null,
    virtual_brand: "", virtual_machine_count: 0, virtual_cpu_core_count: 0, virtual_ram_gb: 0,
    virtual_disk_capacity_tb: 0, application_system_ids: [],
  };
  const out = {};
  DTO_FIELDS.DcHardwareDto.forEach((k) => {
    if (k in derived) out[k] = derived[k];
    else if (row[k] !== undefined && row[k] !== null) out[k] = row[k];
    else out[k] = k in DEFAULTS ? DEFAULTS[k] : null;
  });
  out.id = row.id;
  return out;
}
